/**
 * Knowledge base for the BestAcco Assistant — a client-side help center.
 *
 * There is NO LLM / external API call. The assistant is a curated, topic-wise
 * help browser: entries are grouped by `category` and browsed by the user, with
 * a search box that filters entries (scored against question + keywords +
 * summary + answer + steps) and category chips so people can jump topic-wise.
 *
 * Each entry can carry a richer "detailed view":
 *   - `summary`  — a one-line lead shown under the title.
 *   - `where`    — a short "where to find it" trail (sidebar path).
 *   - `steps`    — an ordered, numbered how-to.
 *   - `tip`      — a highlighted tip / gotcha callout.
 *   - `related`  — ids of related entries, shown as clickable links.
 *
 * Entries are grounded in the real product surfaces:
 *   - PMS (hotel shell): Front Desk (Arrivals/Departures/In House), Reservations,
 *     Group Bookings, Guests, Bills/Folio (Master/Room/Food Bill + payment-type
 *     badge), Payments, Housekeeping, Maintenance, Channel Manager + room
 *     mapping, Revenue Management (Rate Calendar, Rate Plans, Room Types, House
 *     Inventory, policies, promotions, packages), Night Audit, Reports/Statistics,
 *     Shift Management (Cash Drop), Property listing editor (/properties/:id/
 *     listing), Guest Engagement, Property Settings, User Management.
 *   - Platform console (super-admin): Owner Leads, Properties + Add Property
 *     (submission / manual / Excel), Listing Review, Bookings, Guest Reviews
 *     (approve → show / hide → hide), Featured Hotels, Storefront content.
 *
 * Labels/button text are quoted verbatim from the UI so answers stay accurate.
 * Keep answers conversational and concrete (name the sidebar item, the button).
 */

/** The topic categories, in the order they should appear in the browser. */
export type Category =
  | 'Getting started'
  | 'Front desk & reservations'
  | 'Billing & payments'
  | 'Housekeeping & maintenance'
  | 'Channel manager & OTAs'
  | 'Revenue & rates'
  | 'Night audit & reports'
  | 'Shift & cash'
  | 'Property & listing'
  | 'Team & access'
  | 'Platform console (BestAcco)'
  | 'Tips & troubleshooting';

/** Order in which categories render in the help browser. */
export const CATEGORY_ORDER: Category[] = [
  'Getting started',
  'Front desk & reservations',
  'Billing & payments',
  'Housekeeping & maintenance',
  'Channel manager & OTAs',
  'Revenue & rates',
  'Night audit & reports',
  'Shift & cash',
  'Property & listing',
  'Team & access',
  'Platform console (BestAcco)',
  'Tips & troubleshooting',
];

export interface KnowledgeEntry {
  id: string;
  /** The topic title — shown as the row label and the detail heading. */
  question: string;
  /** The category this topic belongs to (drives grouping in the browser). */
  category: Category;
  /** Which shell(s) this topic applies to — filters per variant + labels it. */
  scope: 'pms' | 'admin' | 'both';
  /** Extra search terms (synonyms, related nouns) not necessarily in the text. */
  keywords: string[];
  /** One-line lead shown under the title in the detail view (optional). */
  summary?: string;
  /** Short "where to find it" trail, e.g. 'Sidebar → Front Desk → Arrivals'. */
  where?: string;
  /** Conversational answer. Blank lines separate paragraphs / step lists. */
  answer: string;
  /** Optional ordered, numbered how-to steps shown under the answer. */
  steps?: string[];
  /** Optional highlighted tip / gotcha callout. */
  tip?: string;
  /** Ids of related entries, rendered as clickable links in the detail view. */
  related?: string[];
}

export const KNOWLEDGE_BASE: KnowledgeEntry[] = [
  // ─── Getting started ────────────────────────────────────────────────────────
  {
    id: 'what-is-bestacco',
    question: 'What is BestAcco and what can I do here?',
    category: 'Getting started',
    scope: 'both',
    keywords: ['overview', 'what is', 'getting started', 'intro', 'about', 'help', 'guide', 'pms'],
    summary: 'A property-management system for running hotels, plus a platform console for curating the bestacco.com storefront.',
    answer:
      'BestAcco is a property-management system (PMS) for running hotels end-to-end, plus a platform console the BestAcco team uses to onboard and curate properties for the bestacco.com storefront.\n\n' +
      'In the hotel app you run day-to-day operations: take and manage reservations, check guests in and out at the Front Desk, handle folios and payments in Bills, keep rooms clean in Housekeeping, price and open inventory in Revenue Management, sync to OTAs through Channels, close the day with Night Audit, and reconcile cash in Shift Management.\n\n' +
      'Browse the topics on the left by category to learn any screen, or use the search box to jump straight to one.',
    tip: 'New here? Read this, then "Where do I start each day", then "How do I check a guest in" — that covers the daily loop.',
    related: ['home-hub', 'sidebar-sections', 'check-in'],
  },
  {
    id: 'home-hub',
    question: 'Where do I start each day — Home and Dashboard?',
    category: 'Getting started',
    scope: 'pms',
    keywords: ['home', 'hub', 'dashboard', 'landing', 'start', 'overview', 'tiles', 'kpi'],
    summary: 'Start on the Dashboard to read the day, then work arrivals from the Front Desk.',
    where: 'Sidebar → Home / Dashboard',
    answer:
      '"Home" (the property hub) is a tile launcher grouping every screen by section — Operations, Revenue Management, Administration — so you can jump anywhere with one click.\n\n' +
      '"Dashboard" gives you the at-a-glance numbers for the selected property (occupancy, arrivals/departures, revenue). Most people start on the Dashboard to see the day, then go to "Front Desk" to work arrivals.',
    related: ['switch-property', 'sidebar-sections', 'front-desk-arrivals'],
  },
  {
    id: 'switch-property',
    question: 'How do I switch between properties?',
    category: 'Getting started',
    scope: 'pms',
    keywords: ['switch property', 'change property', 'select property', 'multi property', 'property selector', 'which hotel'],
    summary: 'The active property is shown near the top of the sidebar — click it to switch.',
    where: 'Sidebar → property name (top)',
    answer:
      'If your account has more than one property, the selected property is shown near the top of the sidebar. Click it to switch, or open "Properties" in the Administration section to manage and choose one.\n\n' +
      'Almost every operational screen — Front Desk, Bills, Housekeeping, Rate Calendar — is scoped to the property you have selected, so make sure the right one is active before you start working.',
    tip: 'Seeing the wrong arrivals or empty screens? Check the property selector first — you are probably on another property.',
    related: ['home-hub', 'sidebar-sections'],
  },
  {
    id: 'sidebar-sections',
    question: 'What do the sidebar sections mean (Operations, Revenue, Administration)?',
    category: 'Getting started',
    scope: 'pms',
    keywords: ['sidebar', 'sections', 'menu', 'navigation', 'operations', 'revenue management', 'administration', 'where is', 'find screen', 'grouping'],
    summary: 'The sidebar groups every screen into Operations, Revenue Management, and Administration.',
    where: 'Sidebar',
    answer:
      'The left sidebar groups every screen into three sections so related tools sit together:\n\n' +
      '• "Operations" — the day-to-day desk: Front Desk, Reservations, Group Bookings, Guests, Bills, Housekeeping, Maintenance, Lost & Found, Channels, Night Audit, Reports, Shift Management.\n' +
      '• "Revenue Management" — pricing and inventory: Rate Calendar, Rate Plans, Room Types, House Inventory, and the policies / promotions / packages tools.\n' +
      '• "Administration" — setup: Properties, Property Settings, Guest Engagement, User Management, Charge Types, and other configuration.\n\n' +
      'What you see depends on your role — a role that can\'t price rooms won\'t see the Revenue Management tools.',
    related: ['home-hub', 'roles-permissions', 'quick-search'],
  },
  {
    id: 'quick-search',
    question: 'How do I find a screen or a guest quickly?',
    category: 'Getting started',
    scope: 'both',
    keywords: ['search', 'find', 'jump', 'quick', 'lookup', 'shortcut', 'where is', 'navigate fast', 'find guest'],
    summary: 'Use this help search for how-to, the sidebar to reach a screen, and each screen\'s own search to find records.',
    answer:
      'There are three different "searches", and picking the right one saves time:\n\n' +
      '• This help center (the "?" button) — search here for how to do something ("how do I check out", "sync availability").\n' +
      '• The sidebar — to reach a screen, find its item under Operations / Revenue Management / Administration.\n' +
      '• The screen\'s own search — to find a record, use the search box on that screen (Reservations searches bookings, Guests searches profiles, Bills searches folios).',
    related: ['sidebar-sections', 'reservations', 'guests'],
  },

  // ─── Front desk & reservations ──────────────────────────────────────────────
  {
    id: 'front-desk-arrivals',
    question: 'How do I see arrivals and departures at the front desk?',
    category: 'Front desk & reservations',
    scope: 'pms',
    keywords: ['front desk', 'arrivals', 'departures', 'in house', 'expected', 'by date', 'today', 'due in', 'due out'],
    summary: 'Front Desk has Arrivals, Departures and In House tabs, scoped to a date you pick.',
    where: 'Sidebar → Front Desk',
    answer:
      'Open "Front Desk" in the sidebar. It has three tabs: "Arrivals", "Departures" and "In House".\n\n' +
      'Pick the date with the date picker at the top — "Arrivals" then lists everyone due to check in that day, "Departures" lists those due to check out, and "In House" shows everyone currently staying. Click any reservation to open it and check the guest in or out.',
    steps: [
      'Open "Front Desk" in the sidebar.',
      'Choose the date with the date picker at the top.',
      'Switch between the "Arrivals", "Departures" and "In House" tabs.',
      'Click a reservation to open it and take action.',
    ],
    related: ['check-in', 'check-out', 'reservations'],
  },
  {
    id: 'check-in',
    question: 'How do I check a guest in?',
    category: 'Front desk & reservations',
    scope: 'pms',
    keywords: ['check in', 'check-in', 'checkin', 'arrive', 'assign room', 'register', 'walk in', 'walk-in'],
    summary: 'Open the arrival, confirm details, assign a room, then use the check-in action.',
    where: 'Sidebar → Front Desk → Arrivals',
    answer:
      'Open "Front Desk", stay on the "Arrivals" tab for today, and click the guest\'s reservation to open it. Confirm the guest and stay details, assign a room if one is not set yet, then use the check-in action to move them to In House.\n\n' +
      'Check the payment-type badge on the booking first: "Prepaid via OTA" means collect nothing, "Collect at desk" means take payment now. For a walk-in with no booking, create the reservation first, then check them in the same way.',
    steps: [
      'Open "Front Desk" → "Arrivals" and pick today\'s date.',
      'Click the guest\'s reservation to open it.',
      'Confirm the guest and stay details.',
      'Assign a room if one is not set yet.',
      'Check the payment-type badge and collect payment if it says "Collect at desk".',
      'Use the check-in action — the guest moves to "In House".',
    ],
    tip: 'For a walk-in with no booking, create the reservation first, then check them in exactly the same way.',
    related: ['payment-type', 'check-out', 'new-reservation'],
  },
  {
    id: 'check-out',
    question: 'How do I check a guest out?',
    category: 'Front desk & reservations',
    scope: 'pms',
    keywords: ['check out', 'check-out', 'checkout', 'depart', 'settle bill', 'leave', 'folio balance'],
    summary: 'Settle the folio balance in Bills, then use the check-out action on the reservation.',
    where: 'Sidebar → Front Desk → Departures',
    answer:
      'Open "Front Desk" and go to the "Departures" tab (or "In House"), then click the guest\'s reservation. Before checking out, open the folio in "Bills" and make sure the balance is settled — post any last charges and take payment.\n\n' +
      'Once the folio is clear, use the check-out action on the reservation. The room drops out of In House and Housekeeping flags it as dirty so it can be turned for the next arrival.',
    steps: [
      'Open "Front Desk" → "Departures" (or "In House").',
      'Click the guest\'s reservation.',
      'Open the folio in "Bills" and post any last charges.',
      'Take payment so the balance reaches zero.',
      'Use the check-out action — the room leaves In House and is flagged dirty.',
    ],
    tip: 'The check-out won\'t leave a clean trail if the folio still has a balance — always settle in Bills first.',
    related: ['bills-folio', 'take-payment-refund', 'housekeeping'],
  },
  {
    id: 'reservations',
    question: 'How do I find and manage a reservation?',
    category: 'Front desk & reservations',
    scope: 'pms',
    keywords: ['reservations', 'booking', 'find reservation', 'search reservation', 'modify booking', 'cancel booking', 'amend'],
    summary: 'Search all bookings in Reservations; open one to change dates, room, or status.',
    where: 'Sidebar → Reservations',
    answer:
      'Open "Reservations" in the sidebar to search across bookings (by guest, dates, confirmation number, etc.). Click a reservation to open its detail, where you can view and manage the stay — change dates, room, or status, or cancel it.\n\n' +
      'For same-day check-in/out work, "Front Desk" is usually faster — its Arrivals/Departures/In House tabs are scoped to a chosen date.',
    related: ['new-reservation', 'amend-stay', 'front-desk-arrivals'],
  },
  {
    id: 'new-reservation',
    question: 'How do I create a new reservation or walk-in?',
    category: 'Front desk & reservations',
    scope: 'pms',
    keywords: ['new reservation', 'create booking', 'walk in', 'walk-in', 'add booking', 'book room', 'new stay', 'make reservation'],
    summary: 'Create the booking with room type, dates and guest, then check the walk-in straight in.',
    where: 'Sidebar → Reservations / Front Desk',
    answer:
      'To take a booking directly (phone, email or walk-in), create a new reservation and enter the stay: room type, check-in and check-out dates, and the guest\'s details. Pick a rate plan so the price is set, then save it.\n\n' +
      'For a walk-in who is staying now, create the reservation for today and then check them in from "Front Desk" → "Arrivals" like any other arrival.',
    steps: [
      'Start a new reservation from "Reservations" (or "Front Desk").',
      'Enter room type, check-in / check-out dates and the guest.',
      'Choose a rate plan so the stay is priced.',
      'Save the reservation.',
      'For a walk-in, open it in Front Desk → Arrivals and check them in.',
    ],
    related: ['check-in', 'reservations', 'payment-type'],
  },
  {
    id: 'amend-stay',
    question: "How do I change a stay's dates, room, or cancel it?",
    category: 'Front desk & reservations',
    scope: 'pms',
    keywords: ['amend', 'modify', 'change dates', 'extend stay', 'shorten stay', 'move room', 'room change', 'cancel', 'edit booking'],
    summary: 'Open the reservation and edit dates, room or status; cancelling frees the inventory.',
    where: 'Sidebar → Reservations → open a booking',
    answer:
      'Open the booking from "Reservations" (or Front Desk) and edit it: change the dates to extend or shorten the stay, or move the guest to a different room or room type. Save and the folio and inventory update to match.\n\n' +
      'To cancel, set the reservation\'s status to cancelled — this releases the room back to availability. Check any cancellation policy or deposit before you do, as it may affect what the guest owes.',
    tip: 'Extending or moving a room can change the price — re-check the folio balance in Bills after you amend.',
    related: ['reservations', 'policies-promos-packages', 'bills-folio'],
  },
  {
    id: 'group-bookings',
    question: 'How do I handle a group booking or room block?',
    category: 'Front desk & reservations',
    scope: 'pms',
    keywords: ['group', 'block', 'group bookings', 'rooming list', 'master', 'wedding', 'corporate block', 'allotment'],
    summary: 'A group ties several rooms to one master so you manage the block together.',
    where: 'Sidebar → Group Bookings',
    answer:
      'Open "Group Bookings" in the sidebar. A group ties several rooms to one master so you can manage the block together — hold the rooms, build the rooming list, and track pickup against the block.\n\n' +
      'Individual stays inside the group are still worked at the Front Desk like any other arrival; the group master is where you manage the block as a whole and its billing.',
    related: ['reservations', 'bills-folio', 'companies-direct-bill'],
  },
  {
    id: 'guests',
    question: 'Where do I find guest profiles and history?',
    category: 'Front desk & reservations',
    scope: 'pms',
    keywords: ['guests', 'guest profile', 'history', 'repeat guest', 'contact', 'preferences', 'crm'],
    summary: 'Guests holds profiles — contact details, stay history and preferences.',
    where: 'Sidebar → Guests',
    answer:
      'Open "Guests" in the sidebar to look up guest profiles — contact details, stay history and preferences. Use it to spot a returning guest, correct a profile, or check what someone booked before.\n\n' +
      'Guest records also link out from any reservation, so you can jump from a booking to the full profile.',
    related: ['reservations', 'guest-engagement'],
  },

  // ─── Billing & payments ─────────────────────────────────────────────────────
  {
    id: 'bills-folio',
    question: "Where do I find a guest's bill or folio?",
    category: 'Billing & payments',
    scope: 'pms',
    keywords: ['bills', 'folio', 'invoice', 'charges', 'ledger', 'guest bill', 'settle', 'balance'],
    summary: 'Bills lists folios; open one to see charges and payments and settle the balance.',
    where: 'Sidebar → Bills',
    answer:
      'Open "Bills" in the sidebar — it lists folios for in-house and recent guests. Search by guest or room and open a folio to see charges and payments, post charges, and settle the balance.\n\n' +
      'You can also reach a booking\'s folio straight from the reservation in Front Desk / Reservations.',
    related: ['folio-tabs', 'post-charge', 'take-payment-refund'],
  },
  {
    id: 'folio-tabs',
    question: 'What are the Master / Room / Food bill tabs?',
    category: 'Billing & payments',
    scope: 'pms',
    keywords: ['master bill', 'room bill', 'food bill', 'tabs', 'split bill', 'itemised', 'sections', 'folio tabs'],
    summary: 'Master Bill is the whole folio; Room and Food bills split out each side of the charges.',
    where: 'Sidebar → Bills → open a folio',
    answer:
      'Inside a folio in "Bills" there are three tabs:\n\n' +
      '• "Master Bill" — the whole folio in one view: room & stay, food & beverage, taxes and other charges, with the authoritative balance and payment summary.\n' +
      '• "Room Bill" — just the room-side charges (room charge, extra bed, late check-out, parking, etc.).\n' +
      '• "Food Bill" — the food & beverage lines (room service, breakfast, minibar, and so on).\n\n' +
      'Use Master Bill to settle and print; use Room/Food to review or add charges to a specific side of the bill.',
    related: ['bills-folio', 'post-charge', 'print-invoice'],
  },
  {
    id: 'post-charge',
    question: 'How do I add a charge to a folio?',
    category: 'Billing & payments',
    scope: 'pms',
    keywords: ['post charge', 'add charge', 'add item', 'extra', 'food charge', 'minibar', 'incidental', 'folio item'],
    summary: 'Add a folio item on the Room or Food bill tab — the Master Bill and balance update at once.',
    where: 'Sidebar → Bills → Room Bill / Food Bill',
    answer:
      'Open the folio in "Bills", go to the "Room Bill" or "Food Bill" tab depending on the charge, and add a folio item — pick the category (e.g. Room Charge, Room Service, Food & Beverage), enter the amount and a note, and save.\n\n' +
      'The line appears on the Master Bill and updates the balance immediately. The charge categories themselves come from "Charge Types" in Administration.',
    steps: [
      'Open the folio in "Bills".',
      'Go to "Room Bill" (room-side) or "Food Bill" (F&B).',
      'Add a folio item and pick its charge category.',
      'Enter the amount and a note, then save.',
      'Check it on the "Master Bill" — the balance updates immediately.',
    ],
    related: ['folio-tabs', 'charge-types', 'take-payment-refund'],
  },
  {
    id: 'charge-types',
    question: 'What are Charge Types and how do I add one?',
    category: 'Billing & payments',
    scope: 'pms',
    keywords: ['charge types', 'charge categories', 'posting codes', 'transaction codes', 'add charge type', 'tax on charge', 'setup charges'],
    summary: 'Charge Types are the categories you pick when posting a charge — configured in Administration.',
    where: 'Sidebar → Administration → Charge Types',
    answer:
      'Open "Charge Types" in the Administration section. These are the categories that appear when you post a folio item (Room Charge, Room Service, Food & Beverage, Parking, and so on), so keeping them tidy keeps your reports clean.\n\n' +
      'Add or edit a charge type here — its name and how it behaves — and it becomes available to pick when posting a charge in Bills.',
    related: ['post-charge', 'reports'],
  },
  {
    id: 'payment-type',
    question: "Where do I see a booking's payment status?",
    category: 'Billing & payments',
    scope: 'pms',
    keywords: ['payment status', 'payment type', 'prepaid', 'ota', 'collect at desk', 'pay at hotel', 'bill to company', 'badge', 'who pays'],
    summary: 'A coloured payment-type badge on every booking says who pays and when.',
    where: 'Front Desk / booking detail → payment-type badge',
    answer:
      'Every booking carries a coloured payment-type badge (on the Front Desk arrival / booking detail):\n\n' +
      '• "Prepaid via OTA" (green) — already paid through the channel; collect nothing at the desk.\n' +
      '• "Collect at desk" (amber) — take payment from the guest at check-in.\n' +
      '• "Bill to company" (blue) — charges go to a company account, not the guest.\n' +
      '• "Payment: not set" (grey) — not classified yet.\n\n' +
      'To set or change it, open the booking\'s detail and use the payment-type control (the badge with a dropdown) — pick the right option and it saves immediately.',
    tip: 'Always read this badge before taking money at check-in — collecting on a "Prepaid via OTA" booking double-charges the guest.',
    related: ['check-in', 'take-payment-refund', 'companies-direct-bill'],
  },
  {
    id: 'take-payment-refund',
    question: 'How do I take a payment or issue a refund?',
    category: 'Billing & payments',
    scope: 'pms',
    keywords: ['payment', 'refund', 'take payment', 'settle', 'card', 'cash', 'void', 'reverse', 'tender', 'pay'],
    summary: 'Record payments against the folio\'s Master Bill; a refund is a negative payment.',
    where: 'Sidebar → Bills → Master Bill',
    answer:
      'Payments are recorded against the folio in "Bills". Open the folio\'s "Master Bill", record the payment for the outstanding balance and choose how it was tendered (cash, card, etc.); the balance drops to zero once fully paid.\n\n' +
      'A refund is recorded the same way as a negative/return payment against the folio. Cash movements are also reflected in your shift — reconcile them in "Shift Management" at handover.',
    steps: [
      'Open the folio in "Bills" and go to "Master Bill".',
      'Record a payment for the outstanding balance.',
      'Choose how it was tendered (cash, card, etc.).',
      'For a refund, record a negative / return payment the same way.',
      'Reconcile cash in "Shift Management" at handover.',
    ],
    related: ['bills-folio', 'shift-management', 'payment-type'],
  },
  {
    id: 'print-invoice',
    question: 'How do I print or email an invoice or receipt?',
    category: 'Billing & payments',
    scope: 'pms',
    keywords: ['print', 'invoice', 'receipt', 'email bill', 'pdf', 'tax invoice', 'send bill', 'gst invoice'],
    summary: 'Settle the folio, then print or share the Master Bill as the guest\'s invoice.',
    where: 'Sidebar → Bills → Master Bill',
    answer:
      'The "Master Bill" tab of a folio is the printable, authoritative bill — it shows every charge, taxes, payments and the balance. Once the folio is settled, print or share it from there as the guest\'s invoice or receipt.\n\n' +
      'For a company that is being billed directly, the invoice is raised from the company\'s account instead — see "How do company accounts and direct bill work?"',
    related: ['folio-tabs', 'companies-direct-bill', 'take-payment-refund'],
  },
  {
    id: 'companies-direct-bill',
    question: 'How do company accounts and direct bill work?',
    category: 'Billing & payments',
    scope: 'pms',
    keywords: ['company', 'direct bill', 'accounts receivable', 'ar', 'invoice company', 'bill to company', 'corporate account', 'city ledger'],
    summary: 'Bookings marked "Bill to company" route charges to that company\'s AR ledger.',
    where: 'Sidebar → Companies & Direct Bill',
    answer:
      'Open "Companies & Direct Bill" in the sidebar to manage company accounts, their accounts-receivable balances and invoices. When a booking is marked "Bill to company", its charges route to that company\'s ledger instead of the guest.\n\n' +
      'From here you invoice the company and track what they owe. Non-guest ledgers that are not companies live under "House Accounts".',
    related: ['payment-type', 'print-invoice', 'misc-ledger'],
  },
  {
    id: 'misc-ledger',
    question: 'How do I log a miscellaneous expense or revenue?',
    category: 'Billing & payments',
    scope: 'pms',
    keywords: ['misc expense', 'misc revenue', 'petty cash', 'sundry', 'other income', 'payout', 'expense', 'revenue'],
    summary: 'Misc Expense logs non-guest payouts; Misc Revenue logs non-room income.',
    where: 'Sidebar → Misc Expense / Misc Revenue',
    answer:
      'Use "Misc Expense" to log money paid out that is not tied to a guest folio (supplies, petty-cash payouts), and "Misc Revenue" to record income that is not a room or F&B charge.\n\n' +
      'Both feed your day and shift totals, so log them as they happen to keep Night Audit and the cash reconciliation accurate.',
    related: ['shift-management', 'night-audit', 'cash-drop'],
  },

  // ─── Housekeeping & maintenance ─────────────────────────────────────────────
  {
    id: 'housekeeping',
    question: 'How do I update room status in housekeeping?',
    category: 'Housekeeping & maintenance',
    scope: 'pms',
    keywords: ['housekeeping', 'room status', 'clean', 'dirty', 'inspected', 'service tasks', 'turn room', 'ready'],
    summary: 'Update each room dirty → clean → inspected so the front desk knows what\'s ready.',
    where: 'Sidebar → Housekeeping',
    answer:
      'Open "Housekeeping" in the sidebar to see room status and service tasks. From here you update a room\'s cleaning status (e.g. dirty → clean → inspected) and manage housekeeping tasks so the front desk knows which rooms are ready to assign.\n\n' +
      'The service types available on a task come from "Housekeeping Service Types" in Administration.',
    related: ['hk-tasks', 'maintenance', 'check-out'],
  },
  {
    id: 'hk-tasks',
    question: 'How do I assign and track housekeeping tasks?',
    category: 'Housekeeping & maintenance',
    scope: 'pms',
    keywords: ['housekeeping tasks', 'assign', 'attendant', 'cleaning task', 'service type', 'room attendant', 'track cleaning'],
    summary: 'Create a task on a room, pick the service type, and track it to done.',
    where: 'Sidebar → Housekeeping',
    answer:
      'On the "Housekeeping" screen you create tasks against rooms — pick the service type (from "Housekeeping Service Types" in Administration), and follow it through to completed. As tasks close, the room\'s status updates so the desk can see what\'s ready to assign.\n\n' +
      'Use this to divide the floor across the day and keep an eye on what\'s still outstanding before a busy arrivals window.',
    related: ['housekeeping', 'maintenance'],
  },
  {
    id: 'maintenance',
    question: 'How do I report maintenance or put a room out of order?',
    category: 'Housekeeping & maintenance',
    scope: 'pms',
    keywords: ['maintenance', 'out of order', 'ooo', 'engineering', 'broken', 'repair', 'issue', 'fault', 'block room'],
    summary: 'Log the issue in Maintenance; marking a room out of order removes it from sellable inventory.',
    where: 'Sidebar → Maintenance',
    answer:
      'Open "Maintenance" in the sidebar to log issues, track engineering work and mark rooms out of order. Putting a room out of order takes it out of sellable inventory so it will not be assigned or sold until the issue is cleared.\n\n' +
      'Close the issue when the work is done to return the room to service.',
    tip: 'A room left "out of order" keeps blocking inventory — remember to close the issue once the fix is done.',
    related: ['housekeeping', 'house-inventory'],
  },
  {
    id: 'lost-found',
    question: 'Where do I log lost-and-found items?',
    category: 'Housekeeping & maintenance',
    scope: 'pms',
    keywords: ['lost and found', 'lost found', 'left behind', 'items', 'belongings', 'lost property'],
    summary: 'Lost & Found records items guests leave behind until they are returned or disposed of.',
    where: 'Sidebar → Lost & Found',
    answer:
      'Open "Lost & Found" in the sidebar to record items guests leave behind — what it is, which room, and when it was found — and to track them until they are returned or disposed of.',
    related: ['housekeeping', 'guests'],
  },

  // ─── Channel manager & OTAs ─────────────────────────────────────────────────
  {
    id: 'connect-channel',
    question: 'How do I connect a channel (AxisRooms / OTA)?',
    category: 'Channel manager & OTAs',
    scope: 'pms',
    keywords: ['connect channel', 'axisrooms', 'ota', 'channel manager', 'setup', 'credentials', 'connect', 'integration'],
    summary: 'Set up the channel\'s credentials so its card reads "Connected", then map rooms.',
    where: 'Sidebar → Channels',
    answer:
      'Open "Channels" (Channel Manager) in the sidebar. Each channel shows a connection card — set it up with its credentials so it reads "Connected" rather than "Disconnected".\n\n' +
      'Once connected, the next step is room mapping: link each of your PMS rooms to the channel\'s room ID before availability and rates will flow. See "Why is AxisRooms not syncing availability?" for the mapping details.',
    steps: [
      'Open "Channels" in the sidebar.',
      'On the channel\'s card, enter its credentials.',
      'Confirm the card reads "Connected", not "Disconnected".',
      'Set up room mapping so availability and rates can flow.',
    ],
    related: ['channel-mapping', 'ari-sync', 'sync-log'],
  },
  {
    id: 'channel-mapping',
    question: 'Why is AxisRooms not syncing availability?',
    category: 'Channel manager & OTAs',
    scope: 'pms',
    keywords: ['not syncing', 'availability not syncing', 'room mapping', 'map rooms', 'sync failed', 'no room mapped', 'ari', 'sync now', 'channels'],
    summary: 'Almost always unmapped rooms — each PMS room must be linked to a channel room ID.',
    where: 'Sidebar → Channels → Set up room mapping',
    answer:
      "Almost always it's unmapped rooms. Availability can't sync until each of your rooms is linked to a channel room ID on the OTA.\n\n" +
      'Open "Channels" (Channel Manager). On the channel\'s card, if it shows "No rooms mapped yet", click "Set up room mapping" (or "Mappings") and map each PMS room to its AxisRooms room. Once every room is mapped, use "Sync Now" — the sync activity log will show success instead of a "No room ID mapped" failure.\n\n' +
      'Also confirm the connection shows "Connected" (not Disconnected) before syncing.',
    steps: [
      'Open "Channels" and find the channel\'s card.',
      'If it says "No rooms mapped yet", click "Set up room mapping".',
      'Map each PMS room to its AxisRooms room ID.',
      'Confirm the connection reads "Connected".',
      'Click "Sync Now" and check the sync activity log for success.',
    ],
    tip: 'A "No room ID mapped" line in the sync log points straight at the room that still needs mapping.',
    related: ['connect-channel', 'ari-sync', 'sync-log'],
  },
  {
    id: 'ari-sync',
    question: 'What is ARI and how do rates/availability push to OTAs?',
    category: 'Channel manager & OTAs',
    scope: 'pms',
    keywords: ['ari', 'availability rates inventory', 'push', 'sync now', 'update ota', 'rate push', 'inventory push'],
    summary: 'ARI is Availability, Rates and Inventory — the data BestAcco pushes to connected OTAs.',
    where: 'Sidebar → Channels → Sync Now',
    answer:
      'ARI is Availability, Rates and Inventory — the data BestAcco pushes out to each connected OTA. When you change a rate in the Rate Calendar or open/close inventory, that update is queued and sent to the mapped channels.\n\n' +
      'You can force a push from "Channels" with "Sync Now", and the sync activity log shows whether each push succeeded. If a channel is out of step, check the log there first.',
    related: ['channel-mapping', 'sync-log', 'rates-availability'],
  },
  {
    id: 'sync-log',
    question: 'How do I read the sync activity log and fix a failed push?',
    category: 'Channel manager & OTAs',
    scope: 'pms',
    keywords: ['sync log', 'activity log', 'failed push', 'sync error', 'sync history', 'push failed', 'troubleshoot sync', 'why failed'],
    summary: 'The sync activity log shows each push and its result — the reason tells you what to fix.',
    where: 'Sidebar → Channels → sync activity log',
    answer:
      'On "Channels", the sync activity log lists each availability / rate push and whether it succeeded or failed, with a reason. Reading the reason is the fastest way to fix a channel that\'s out of step:\n\n' +
      '• "No room ID mapped" → finish room mapping for that room.\n' +
      '• "Disconnected" / auth errors → re-check the channel\'s credentials.\n' +
      '• A changed price or closed date not reflected on the OTA → click "Sync Now" to force the push and watch for a success line.',
    related: ['channel-mapping', 'ari-sync', 'connect-channel'],
  },
  {
    id: 'ota-bookings',
    question: 'How do OTA bookings arrive in the PMS?',
    category: 'Channel manager & OTAs',
    scope: 'pms',
    keywords: ['ota booking', 'incoming booking', 'booking.com', 'reservation from channel', 'auto import', 'prepaid via ota', 'multi room'],
    summary: 'OTA bookings flow in automatically as reservations and block the inventory they sold.',
    where: 'Sidebar → Reservations / Front Desk',
    answer:
      'Bookings made on a connected OTA flow into the PMS automatically and appear as reservations in "Reservations" and at the "Front Desk" on their arrival date — you don\'t key them in by hand. The inventory they sold is blocked so the same room can\'t be double-sold.\n\n' +
      'These usually carry the "Prepaid via OTA" payment-type badge, so at check-in you collect nothing unless the badge says otherwise. A multi-room OTA booking comes in with all its rooms booked and blocked together.',
    tip: 'If an expected OTA booking is missing, check the channel is "Connected" and rooms are mapped — an unmapped channel can\'t deliver reservations reliably.',
    related: ['payment-type', 'channel-mapping', 'reservations'],
  },

  // ─── Revenue & rates ────────────────────────────────────────────────────────
  {
    id: 'rates-availability',
    question: 'How do I set rates and availability?',
    category: 'Revenue & rates',
    scope: 'pms',
    keywords: ['rates', 'rate calendar', 'availability', 'pricing', 'restrictions', 'inventory', 'price', 'stop sell'],
    summary: 'The Rate Calendar is the grid where you set rates and restrictions by date and plan.',
    where: 'Sidebar → Revenue Management → Rate Calendar',
    answer:
      'Use the Revenue Management section of the sidebar. "Rate Calendar" is the main grid — set rates and restrictions (min-stay, stop-sell, close-to-arrival) by date and rate plan.\n\n' +
      '"Rate Administration" holds your broader pricing strategy and overrides. Changes here flow out to connected OTAs as ARI updates.',
    related: ['bulk-rates', 'stop-sell', 'rate-plans', 'ari-sync'],
  },
  {
    id: 'bulk-rates',
    question: 'How do I update rates across many dates at once?',
    category: 'Revenue & rates',
    scope: 'pms',
    keywords: ['bulk', 'bulk update', 'many dates', 'date range', 'update rates', 'weekend rates', 'apply to range', 'mass update'],
    summary: 'Set a rate over a date range and rate plan in the Rate Calendar rather than day by day.',
    where: 'Sidebar → Revenue Management → Rate Calendar',
    answer:
      'In the "Rate Calendar", set a rate for a range of dates and a chosen rate plan rather than editing each day. Pick the rate plan and the date span, enter the price (and any restrictions), and apply — every date in the range updates together and is queued to the connected OTAs.\n\n' +
      'This is the quick way to price a season, a weekend pattern, or an event window in one pass.',
    tip: 'After a bulk change, glance at the sync activity log in "Channels" to confirm the new prices pushed to your OTAs.',
    related: ['rates-availability', 'stop-sell', 'ari-sync'],
  },
  {
    id: 'stop-sell',
    question: 'How do I stop-sell or close a date?',
    category: 'Revenue & rates',
    scope: 'pms',
    keywords: ['stop sell', 'close', 'close out', 'closed to arrival', 'cta', 'min stay', 'restriction', 'block sales', 'sold out'],
    summary: 'Apply a stop-sell / close-to-arrival restriction on the date in the Rate Calendar.',
    where: 'Sidebar → Revenue Management → Rate Calendar',
    answer:
      'In the "Rate Calendar", open the date and rate plan and apply the restriction: "stop-sell" closes the date to new bookings, "close-to-arrival" blocks arrivals on that date, and "min-stay" sets a minimum length of stay. Save and the restriction pushes to the connected OTAs.\n\n' +
      'Use stop-sell to protect inventory on a sold-out or held date; lift it the same way once you want to sell again.',
    related: ['rates-availability', 'bulk-rates', 'house-inventory'],
  },
  {
    id: 'rate-plans',
    question: 'What are rate plans and how do I set them up?',
    category: 'Revenue & rates',
    scope: 'pms',
    keywords: ['rate plans', 'derived', 'bar', 'best available rate', 'refundable', 'non refundable', 'plan', 'derivation'],
    summary: 'Rate plans are the products you sell (BAR, non-refundable, breakfast-included); some derive from a base.',
    where: 'Sidebar → Revenue Management → Rate Plans',
    answer:
      'Open "Rate Plans" in the sidebar to define the plans you sell — for example a Best Available Rate, a non-refundable rate, or a breakfast-included plan. Plans can be derived from a base plan (e.g. "BAR minus 10%") so they move together.\n\n' +
      'Rate plans are what you then price by date in the "Rate Calendar", and what channels map to.',
    related: ['rates-availability', 'room-types', 'policies-promos-packages'],
  },
  {
    id: 'room-types',
    question: 'How do I manage room types?',
    category: 'Revenue & rates',
    scope: 'pms',
    keywords: ['room types', 'room category', 'inventory type', 'deluxe', 'suite', 'add room type', 'categories'],
    summary: 'Room Types are your sellable categories — the backbone of inventory, rates and mappings.',
    where: 'Sidebar → Revenue Management → Room Types',
    answer:
      'Open "Room Types" in the sidebar to define your room categories (e.g. Standard, Deluxe, Suite) — the units you sell and price. Room types are the backbone of inventory: the House Inventory grid, rate plans and channel mappings are all organised by them.',
    related: ['house-inventory', 'rate-plans', 'add-room'],
  },
  {
    id: 'house-inventory',
    question: 'Where do I see availability by room type and date?',
    category: 'Revenue & rates',
    scope: 'pms',
    keywords: ['house inventory', 'inventory grid', 'availability grid', 'rooms sold', 'left to sell', 'occupancy grid', 'open close'],
    summary: 'House Inventory is the grid of rooms sold vs. available by room type and date.',
    where: 'Sidebar → Revenue Management → House Inventory',
    answer:
      'Open "House Inventory" in the sidebar for the availability grid — rooms sold vs. available by room type and date. It is where you see how much is left to sell and open or close inventory day by day.',
    related: ['room-types', 'stop-sell', 'rates-availability'],
  },
  {
    id: 'policies-promos-packages',
    question: 'How do cancellation policies, promo codes and packages work?',
    category: 'Revenue & rates',
    scope: 'pms',
    keywords: ['cancellation policy', 'deposit policy', 'promotion code', 'promo', 'discount', 'packages', 'guarantee', 'inclusions'],
    summary: 'Policies, deposits, promo codes and packages are set up once and attached to rate plans.',
    where: 'Sidebar → Revenue Management',
    answer:
      'These all live in Revenue Management:\n\n' +
      '• "Cancellation Policies" — cancellation and guarantee rules attached to your rates.\n' +
      '• "Deposit Policies" — advance-deposit rules and timing.\n' +
      '• "Promotion Codes" — discount codes guests apply at booking.\n' +
      '• "Packages" — bundled rate packages with inclusions (e.g. room + breakfast + parking).\n\n' +
      'Set them up once and attach them to the relevant rate plans.',
    related: ['rate-plans', 'rates-availability'],
  },

  // ─── Night audit & reports ──────────────────────────────────────────────────
  {
    id: 'night-audit',
    question: 'What does Night Audit do and how do I run it?',
    category: 'Night audit & reports',
    scope: 'pms',
    keywords: ['night audit', 'roll the day', 'business day', 'end of day', 'post charges', 'audit', 'close day', 'eod'],
    summary: 'Night Audit posts the day\'s charges and rolls the business day forward — the end-of-day close.',
    where: 'Sidebar → Night Audit',
    answer:
      'Open "Night Audit" in the sidebar. It rolls the business day forward and posts the day\'s charges (e.g. room & tax) — the end-of-day close.\n\n' +
      'Run it once per day after the day\'s activity is settled; follow the on-screen steps and checks before confirming the roll. After it runs, the new business day begins.',
    steps: [
      'Settle the day\'s activity — payments taken, charges posted, cash reconciled.',
      'Open "Night Audit" in the sidebar.',
      'Work through the on-screen checks it presents.',
      'Confirm the roll — the day\'s charges post and the new business day begins.',
    ],
    related: ['night-audit-fail', 'shift-management', 'reports'],
  },
  {
    id: 'night-audit-fail',
    question: 'What if Night Audit fails or a day was missed?',
    category: 'Night audit & reports',
    scope: 'pms',
    keywords: ['audit failed', 'missed audit', 'skipped night audit', 'catch up', 'audit error', 'behind', 'run again', 'stuck'],
    summary: 'Clear what the checks flag, then run Night Audit again; missed days are rolled one at a time.',
    where: 'Sidebar → Night Audit',
    answer:
      'If a Night Audit doesn\'t complete, it usually stops on a check — for example an unsettled folio or an open shift. Clear what it flags (settle the folio, reconcile the shift) and run it again.\n\n' +
      'If a day was missed entirely, the business day is still behind, so run the audit to roll forward — you may need to run it once per outstanding day until you\'re caught up to the current date.',
    tip: 'Because Night Audit posts room & tax, running it late or twice can shift where charges land — reconcile the folios and shift before you confirm.',
    related: ['night-audit', 'shift-management', 'bills-folio'],
  },
  {
    id: 'reports',
    question: 'Where are the reports and statistics?',
    category: 'Night audit & reports',
    scope: 'pms',
    keywords: ['reports', 'statistics', 'exports', 'data extract', 'occupancy', 'revenue report', 'scheduler', 'csv'],
    summary: 'Reports holds the dynamic reports; Statistics, Report Scheduler and Data Extracts sit alongside.',
    where: 'Sidebar → Operations → Reports',
    answer:
      'Open "Reports" in the sidebar for the dynamic reports and statistics. Related tools sit alongside it in Operations:\n\n' +
      '• "Report Scheduler" — queue and schedule report runs.\n' +
      '• "Data Extracts" — custom data extracts and CSV exports.\n' +
      '• "Statistics" — revenue and rooms-sold statistics at a glance.',
    related: ['schedule-report', 'audit-log', 'night-audit'],
  },
  {
    id: 'schedule-report',
    question: 'How do I schedule a report to email automatically?',
    category: 'Night audit & reports',
    scope: 'pms',
    keywords: ['schedule report', 'automatic report', 'email report', 'recurring', 'report scheduler', 'daily report', 'send report'],
    summary: 'Use Report Scheduler to queue a report to run and send on a recurring schedule.',
    where: 'Sidebar → Operations → Report Scheduler',
    answer:
      'Open "Report Scheduler" in the Operations section. Set up a report to run on a schedule — pick the report, the frequency (e.g. daily), and who receives it — so it lands in inboxes automatically without anyone running it by hand.\n\n' +
      'For one-off pulls or custom columns, use "Data Extracts" to export a CSV instead.',
    related: ['reports', 'audit-log'],
  },
  {
    id: 'audit-log',
    question: 'Is there an audit trail of who changed what?',
    category: 'Night audit & reports',
    scope: 'pms',
    keywords: ['audit log', 'activity log', 'who changed', 'history', 'trail', 'accountability', 'changes'],
    summary: 'Yes — the system keeps a trail of actions so you can see who did what and when.',
    answer:
      'Yes — the system keeps an audit trail of actions taken across the app so you can see who did what and when. Use it to trace a change, confirm an override, or investigate a discrepancy.',
    related: ['reports', 'roles-permissions'],
  },

  // ─── Shift & cash ───────────────────────────────────────────────────────────
  {
    id: 'shift-management',
    question: 'How does Shift Management and the shift summary work?',
    category: 'Shift & cash',
    scope: 'pms',
    keywords: ['shift', 'shift management', 'handover', 'cashier', 'payment summary', 'reconcile', 'close shift', 'open shift'],
    summary: 'The Summary tab totals payments taken during the shift by tender so cashiers reconcile at handover.',
    where: 'Sidebar → Shift Management → Summary',
    answer:
      'Open "Shift Management" in the sidebar. The "Summary" tab shows the payments taken during the shift by tender type, so a cashier can reconcile cash and cards at handover.\n\n' +
      'Reconcile at the end of every shift so the next cashier starts clean and the day balances at Night Audit.',
    related: ['cash-drop', 'take-payment-refund', 'night-audit'],
  },
  {
    id: 'cash-drop',
    question: 'How do I do a cash drop?',
    category: 'Shift & cash',
    scope: 'pms',
    keywords: ['cash drop', 'drop cash', 'safe', 'deposit cash', 'till', 'skim', 'cash drop details'],
    summary: 'The Cash Drop tab records cash moved from the till to the safe; Cash Drop Details lists past drops.',
    where: 'Sidebar → Shift Management → Cash Drop',
    answer:
      'Open "Shift Management" and go to the "Cash Drop" tab to record cash moved from the till to the safe during a shift. Enter the amount being dropped and confirm it.\n\n' +
      'The "Cash Drop Details" tab lists the drops already recorded, so you can review or reconcile them against the safe count.',
    related: ['shift-management', 'misc-ledger'],
  },

  // ─── Property & listing ─────────────────────────────────────────────────────
  {
    id: 'listing-editor',
    question: 'How do I edit my property listing (rooms, photos, amenities)?',
    category: 'Property & listing',
    scope: 'pms',
    keywords: ['listing', 'edit listing', 'listing editor', 'overview', 'guidelines', 'faq', 'amenities', 'photos', 'video', 'storefront listing', 'self edit'],
    summary: 'The listing editor has five tabs — Overview, Guidelines & FAQ, Rooms & rates, Amenities, Photos & video.',
    where: 'Sidebar → Properties → your property → Listing (/properties/:id/listing)',
    answer:
      'Open "Properties", click your property and open its listing editor (at /properties/:id/listing). It has five tabs:\n\n' +
      '• "Overview" — the headline description and key details.\n' +
      '• "Guidelines & FAQ" — house rules, check-in/out times and guest FAQs.\n' +
      '• "Rooms & rates" — the room types you offer and their pricing.\n' +
      '• "Amenities" — the facilities you tick on.\n' +
      '• "Photos & video" — the gallery shown on the storefront.\n\n' +
      'Edits are saved per tab; the header shows who last edited it. This is the content that appears on your bestacco.com listing.',
    related: ['add-room', 'property-photos', 'publish-listing'],
  },
  {
    id: 'property-photos',
    question: 'How do I add or reorder property photos and video?',
    category: 'Property & listing',
    scope: 'both',
    keywords: ['photos', 'images', 'gallery', 'video', 'upload photo', 'reorder', 'cover photo', 'media', 'add pictures'],
    summary: 'Upload images and video from your device on the Photos & video tab — the gallery drives the storefront.',
    where: 'Listing editor → Photos & video',
    answer:
      'Open the listing editor\'s "Photos & video" tab. Upload photos and video from your device — this is the gallery guests see on the bestacco.com listing, so lead with your strongest, well-lit shots. Reorder them so the best image sits first as the cover.\n\n' +
      'Always upload the actual image or video file from your computer or phone; don\'t paste in a link to one hosted elsewhere.',
    tip: 'Every photo, video and ID field in BestAcco is a device file-upload — pick the file, don\'t paste a URL.',
    related: ['listing-editor', 'publish-listing'],
  },
  {
    id: 'publish-listing',
    question: 'How do I get my listing live on bestacco.com?',
    category: 'Property & listing',
    scope: 'both',
    keywords: ['publish', 'go live', 'approve listing', 'live', 'storefront', 'visible', 'submit for review', 'make public'],
    summary: 'Fill in the listing, then the BestAcco team reviews and approves it to publish to the storefront.',
    where: 'Listing editor → Platform console → Listing Review',
    answer:
      'Fill in the listing across all five tabs (Overview, Guidelines & FAQ, Rooms & rates, Amenities, Photos & video) so it\'s complete. The BestAcco platform team then reviews it in the console\'s "Listing Review" and approves it, which publishes it to bestacco.com.\n\n' +
      'Properties added by the platform team can be created as already-approved; either way, "Approved" is the status that makes a listing show on the storefront.',
    related: ['listing-editor', 'listing-review', 'property-photos'],
  },
  {
    id: 'add-room',
    question: 'How do I add rooms to a property?',
    category: 'Property & listing',
    scope: 'both',
    keywords: ['add room', 'add rooms', 'add unit', 'room type', 'multiple rooms', 'new room', 'create room', 'rooms and rates'],
    summary: 'Rooms live inside each property — add them on the Rooms & rates tab, one per real room-type.',
    where: 'Properties → your property → Rooms & rates → Add room / Add Unit',
    answer:
      'Rooms are managed inside each property (not on their own page). Open "Properties", click the property, then open the "Rooms & rates" tab and click "Add room" (in the platform console the action is "Add Unit").\n\n' +
      'For every room set: name, type, capacity, base occupancy (guests included in the base rate), extra-person charge (per guest over base occupancy, per night), and base price. Repeat for each real room-type. Each room here is what you later link to a channel room ID when mapping to AxisRooms, so add one per real room-type before mapping.',
    steps: [
      'Open "Properties" and click the property.',
      'Open the "Rooms & rates" tab.',
      'Click "Add room" (or "Add Unit" in the console).',
      'Set name, type, capacity, base occupancy, extra-person charge and base price.',
      'Repeat for each real room-type — you\'ll map these to channel room IDs later.',
    ],
    related: ['room-types', 'channel-mapping', 'listing-editor'],
  },
  {
    id: 'property-settings',
    question: 'Where do I change property details and policies?',
    category: 'Property & listing',
    scope: 'pms',
    keywords: ['property settings', 'policies', 'details', 'tax', 'check in time', 'configuration', 'engagement', 'property data'],
    summary: 'Property Settings holds the operational config — core data, policies and engagement settings.',
    where: 'Sidebar → Administration → Property Settings',
    answer:
      'Open "Property Settings" in the Administration section to change the property\'s core data, policies and engagement settings. This is the operational configuration (as opposed to the public-facing listing content, which lives in the listing editor).',
    related: ['listing-editor', 'guest-engagement'],
  },
  {
    id: 'guest-engagement',
    question: 'How do I set up confirmation and arrival letters and online check-out?',
    category: 'Property & listing',
    scope: 'pms',
    keywords: ['guest engagement', 'confirmation letter', 'arrival letter', 'online check out', 'email', 'templates', 'lifecycle', 'letters'],
    summary: 'Guest Engagement manages the lifecycle letters and the online check-out setting, per property.',
    where: 'Sidebar → Administration → Guest Engagement',
    answer:
      'Open "Guest Engagement" in the Administration section. It manages the lifecycle letters guests receive — "Confirmation", "Arrival-Day" and the "Online Check-Out" message — as templates you edit per property.\n\n' +
      'There is also an "Enable online check-out" setting so guests can check themselves out. Edit a template and save; the change applies to future sends.',
    related: ['property-settings', 'guests'],
  },

  // ─── Team & access ──────────────────────────────────────────────────────────
  {
    id: 'add-team-user',
    question: 'How do I add a team member?',
    category: 'Team & access',
    scope: 'pms',
    keywords: ['user', 'team', 'invite', 'staff login', 'add account', 'user management', 'add user', 'member', 'colleague'],
    summary: 'Add the person in User Management with a role and the properties they can access.',
    where: 'Sidebar → Administration → User Management',
    answer:
      'Open "User Management" (the Users page) in the Administration section. Add the person\'s email and name, pick a Role (e.g. Revenue Manager, Operations), and grant the properties they can access.\n\n' +
      'They get a login and set their own password on first sign-in. (Property owners are not staff and don\'t get logins.)',
    steps: [
      'Open "User Management" in the Administration section.',
      'Add the person\'s email and name.',
      'Pick a Role (e.g. Revenue Manager, Operations).',
      'Grant the properties they can access.',
      'Save — they set their own password on first sign-in.',
    ],
    related: ['roles-permissions', 'manage-user', 'owners-no-login'],
  },
  {
    id: 'roles-permissions',
    question: 'What do the roles control (who can see what)?',
    category: 'Team & access',
    scope: 'pms',
    keywords: ['roles', 'permissions', 'access', 'revenue manager', 'operations', 'super admin', 'read only', 'restrict', 'what can they see'],
    summary: 'A user\'s role decides which sidebar sections and actions they get; some items are read-only.',
    where: 'Sidebar → Administration → User Management',
    answer:
      'A staff member\'s role decides which sidebar sections and actions they get. For example, an "Operations" user sees Front Desk, Housekeeping and Maintenance but not the full Revenue Management tools, while a "Revenue Manager" gets the rate and pricing screens. Some items are read-only for certain roles.\n\n' +
      'Set the role when you add or edit the user in "User Management" — it takes effect on their next sign-in.',
    related: ['add-team-user', 'manage-user', 'sidebar-sections'],
  },
  {
    id: 'manage-user',
    question: "How do I reset a password or deactivate a team member?",
    category: 'Team & access',
    scope: 'pms',
    keywords: ['reset password', 'deactivate', 'remove user', 'disable account', 'change role', 'revoke access', 'leaver', 'offboard'],
    summary: 'Edit the person in User Management to change their role, reset access, or deactivate them.',
    where: 'Sidebar → Administration → User Management',
    answer:
      'Open "User Management", find the person and open their record. From there you can change their role, adjust which properties they can access, or deactivate the account when they leave so they can no longer sign in.\n\n' +
      'Changes take effect on their next sign-in. Deactivating is the safe way to offboard someone — it removes access without deleting their history in the audit trail.',
    related: ['add-team-user', 'roles-permissions', 'audit-log'],
  },
  {
    id: 'owners-no-login',
    question: 'Do property owners need a login?',
    category: 'Team & access',
    scope: 'both',
    keywords: ['owner login', 'owner account', 'do owners log in', 'invite owner', 'operator login', 'give owner access'],
    summary: 'No — owners don\'t log in; BestAcco runs the property centrally on their behalf.',
    answer:
      "No — owners don't log in to operate the property. BestAcco runs on a central model: the platform team onboards and manages properties on owners' behalf, and owners are reached via the contact details on their lead.\n\n" +
      'Logins are only for internal staff. To give a staff member access, add them in "User Management" — see "How do I add a team member?"',
    related: ['add-team-user', 'owner-leads', 'operator-vs-property'],
  },

  // ─── Platform console (BestAcco) ────────────────────────────────────────────
  {
    id: 'add-property',
    question: 'How do I add a property?',
    category: 'Platform console (BestAcco)',
    scope: 'admin',
    keywords: ['add property', 'new hotel', 'create property', 'onboard property', 'add hotel', 'three ways', 'excel', 'import', 'manual', 'submission'],
    summary: 'Click "Add Property" and start from a submission, a manual form, or an Excel import.',
    where: 'Sidebar → Properties → Add Property',
    answer:
      'Open "Properties" in the sidebar and click "Add Property" — a chooser offers three ways to start:\n\n' +
      '• "From a submission" — start from an owner lead you already received (it pre-fills from the lead).\n' +
      '• "Enter manually" — type the details yourself in a blank form.\n' +
      '• "Import from Excel" — upload the BestAcco onboarding sheet (.xlsx) and the form opens pre-filled from it.\n\n' +
      'Whichever you pick, review the fields (Operator, name, address, city, country, currency) and click "Create Property". It is published as approved and appears under that operator. Enrich it later from the property\'s tabs (Rooms & rates, Photos & video, etc.).',
    steps: [
      'Open "Properties" and click "Add Property".',
      'Choose a start point: from a submission, enter manually, or import from Excel.',
      'Review Operator, name, address, city, country and currency.',
      'Click "Create Property" — it publishes as approved under that operator.',
      'Enrich it from the property\'s tabs (Rooms & rates, Photos & video, …).',
    ],
    related: ['import-excel', 'owner-leads', 'operator-vs-property'],
  },
  {
    id: 'import-excel',
    question: 'How do I import a property from an Excel sheet?',
    category: 'Platform console (BestAcco)',
    scope: 'admin',
    keywords: ['excel', 'xlsx', 'spreadsheet', 'onboarding sheet', 'bulk', 'upload sheet', 'import'],
    summary: 'Add Property → Import from Excel opens the form pre-filled from the onboarding sheet.',
    where: 'Sidebar → Properties → Add Property → Import from Excel',
    answer:
      'Open "Properties", click "Add Property", then choose "Import from Excel" and select the BestAcco onboarding sheet (.xlsx).\n\n' +
      'The Add Property panel opens pre-filled from the sheet, with a banner showing the file name. Review the autofilled fields (Operator, name, address, city, etc.), correct anything, and click "Create Property" — it becomes an approved, live listing you can then flesh out.',
    tip: 'Upload the .xlsx file itself — don\'t retype the sheet; the importer maps its columns for you.',
    related: ['add-property', 'listing-review'],
  },
  {
    id: 'owner-leads',
    question: 'How do I work owner leads?',
    category: 'Platform console (BestAcco)',
    scope: 'admin',
    keywords: ['owner leads', 'submissions', 'interest', 'contact owner', 'onboard', 'listing submission', 'new lead', 'approve onboard'],
    summary: 'Owner Leads are inbound owners; filter by tab, contact them, then Approve & onboard.',
    where: 'Sidebar → Owner Leads',
    answer:
      'Open "Owner Leads" in the sidebar — these are owners who submitted interest in listing.\n\n' +
      'Use the tabs (All / New / Contacted / Onboarded / Closed) to filter, then click a row to open its panel. Contact the owner with the "Email" or "Phone" links.\n\n' +
      'To bring them on, click "Approve & onboard" and confirm — this creates an owner account plus a draft property. You can also "Request changes" (send feedback) or "Reject" (with a reason).',
    steps: [
      'Open "Owner Leads" in the sidebar.',
      'Filter with the tabs (All / New / Contacted / Onboarded / Closed).',
      'Click a row and contact the owner via "Email" or "Phone".',
      'Click "Approve & onboard" and confirm — creates an owner account + draft property.',
      'Or use "Request changes" (feedback) / "Reject" (with a reason).',
    ],
    related: ['add-property', 'listing-review', 'operator-vs-property'],
  },
  {
    id: 'listing-review',
    question: 'How do I review and approve a listing?',
    category: 'Platform console (BestAcco)',
    scope: 'admin',
    keywords: ['listing review', 'review queue', 'approve listing', 'pending review', 'moderate', 'changes requested'],
    summary: 'In Listing Review, open a property, check its content, then Approve or Request changes.',
    where: 'Sidebar → Listing Review',
    answer:
      'Open "Listing Review" in the sidebar. Filter with the tabs (Pending Review / Changes Requested / Approved / Draft) and click a property.\n\n' +
      'Check its details, photos, units, policies and amenities in the "Review property" panel, then click "Approve" to publish it, or "Request changes" and write feedback for the operator.',
    steps: [
      'Open "Listing Review" and filter with the tabs.',
      'Click a property to open the "Review property" panel.',
      'Check details, photos, units, policies and amenities.',
      'Click "Approve" to publish, or "Request changes" with feedback.',
    ],
    related: ['add-property', 'publish-listing', 'guest-reviews'],
  },
  {
    id: 'guest-reviews',
    question: 'How do guest reviews get approved?',
    category: 'Platform console (BestAcco)',
    scope: 'admin',
    keywords: ['guest reviews', 'ratings', 'approve review', 'reject review', 'hide review', 'moderate reviews', 'show review', 'testimonials'],
    summary: 'In Guest Reviews, Approve shows a review on the storefront; Hide takes it down.',
    where: 'Sidebar → Guest Reviews',
    answer:
      'Open "Guest Reviews" in the sidebar and use the tabs (Pending / Approved / Hidden) to browse.\n\n' +
      'Each review has two actions:\n' +
      '• "Approve" — sets it to Approved and it shows on the storefront (bestacco.com).\n' +
      '• "Hide" — sets it to Hidden and it will not show on the site.\n\n' +
      'These write the real review status, so a Pending review only appears publicly once you approve it. You can re-approve a hidden one, or hide an approved one, at any time.',
    tip: 'Approve and Hide write the live status immediately — a Pending review is invisible on bestacco.com until you approve it.',
    related: ['listing-review', 'storefront-content', 'feature-hotel'],
  },
  {
    id: 'admin-bookings',
    question: 'Where do I see bookings across all properties?',
    category: 'Platform console (BestAcco)',
    scope: 'admin',
    keywords: ['bookings', 'guest name', 'party', 'reservations list', 'all bookings', 'platform bookings', 'email'],
    summary: 'The console\'s Bookings screen lists bookings across every operator centrally.',
    where: 'Sidebar → Bookings',
    answer:
      'Open "Bookings" in the sidebar of the platform console. It lists bookings across every operator with the guest name, email and party size, and you can filter/scan them centrally.\n\n' +
      "(For working an individual stay — check-in, folio, payment — use the PMS hotel app's Front Desk and Bills instead.)",
    related: ['operator-vs-property', 'guest-reviews'],
  },
  {
    id: 'operator-vs-property',
    question: 'What is an Operator vs a Property?',
    category: 'Platform console (BestAcco)',
    scope: 'admin',
    keywords: ['operator', 'property', 'difference', 'account', 'owner account', 'parent', 'multi property', 'group'],
    summary: 'An Operator is the owning account; properties are the individual hotels under it.',
    answer:
      'In the platform console an "Operator" is the owning account (the business or owner), and "Properties" are the individual hotels that sit under it. Onboarding an owner lead creates an operator account plus a property; a single operator can hold several properties.\n\n' +
      'When you add a property you assign it to an operator, and console screens like "Bookings" span every operator so you can see the whole platform at once.',
    related: ['owner-leads', 'add-property', 'admin-bookings'],
  },
  {
    id: 'feature-hotel',
    question: 'How do I feature a hotel on the homepage?',
    category: 'Platform console (BestAcco)',
    scope: 'admin',
    keywords: ['featured', 'feature hotel', 'editor pick', 'homepage banner', 'promote', 'spotlight', 'highlight'],
    summary: 'Move a property into the Featured set, reorder it, and set one as the Editor\'s Pick.',
    where: 'Sidebar → Featured Hotels',
    answer:
      'Open "Featured Hotels" in the sidebar. In the "Eligible pool" on the right, find an approved property and click "Feature" to move it into the Featured set.\n\n' +
      'Reorder featured hotels with the up/down arrows — that order drives the homepage grid. Use the star toggle to set one hotel as the "Editor\'s Pick" (the homepage banner).',
    steps: [
      'Open "Featured Hotels" in the sidebar.',
      'In the "Eligible pool", click "Feature" on an approved property.',
      'Reorder featured hotels with the up/down arrows.',
      'Use the star toggle to set the "Editor\'s Pick" (homepage banner).',
    ],
    related: ['storefront-content', 'listing-review', 'guest-reviews'],
  },
  {
    id: 'storefront-content',
    question: 'How do I edit the storefront homepage content?',
    category: 'Platform console (BestAcco)',
    scope: 'admin',
    keywords: ['homepage content', 'storefront', 'marketing', 'hero stats', 'stories', 'video stories', 'landing page', 'website copy'],
    summary: 'Homepage Content edits the hero/difference stats; Storefront Stories adds reviews and video stories.',
    where: 'Sidebar → Homepage Content / Storefront Stories',
    answer:
      'Two places, both in the sidebar:\n\n' +
      '• "Homepage Content" — edit the hero stats (hotels, cities, verified) and "The BestAcco difference" stats. Change a Value/Label and click "Save" on that row; it publishes to visitors immediately.\n' +
      '• "Storefront Stories" — add hotel reviews or video stories (pick a property, add the rating/author/quote or the video poster & URL) and click "Add review" / "Add story".',
    tip: 'Homepage Content saves publish to bestacco.com immediately — double-check the number before you click "Save".',
    related: ['feature-hotel', 'guest-reviews'],
  },

  // ─── Tips & troubleshooting ─────────────────────────────────────────────────
  {
    id: 'dashboard-numbers-wrong',
    question: 'The dashboard numbers look wrong — what do I check?',
    category: 'Tips & troubleshooting',
    scope: 'both',
    keywords: ['numbers wrong', 'dashboard wrong', 'occupancy off', 'revenue wrong', 'not matching', 'discrepancy', 'stats off', 'mismatch'],
    summary: 'Check the property selector, the date, whether Night Audit ran, and unsettled folios.',
    answer:
      'If the dashboard looks off, run through these in order — one of them is almost always it:\n\n' +
      '• Property — confirm the property selector at the top of the sidebar is on the right hotel.\n' +
      '• Date / business day — check the date the screen is showing, and whether "Night Audit" has rolled the day yet; before the audit runs, the day\'s figures aren\'t final.\n' +
      '• Unsettled folios or unposted charges — an open balance or a charge not yet posted skews revenue.\n' +
      '• Shift not reconciled — cash totals can look off until the shift is reconciled in "Shift Management".',
    tip: 'Nine times out of ten it\'s the property selector or a Night Audit that hasn\'t been run yet.',
    related: ['switch-property', 'night-audit', 'shift-management'],
  },
  {
    id: 'not-saving',
    question: "Something isn't saving or I hit an error — what now?",
    category: 'Tips & troubleshooting',
    scope: 'both',
    keywords: ['not saving', 'error', 'failed', 'stuck', 'wont save', 'cant save', 'broken', 'reload', 'try again', 'permission'],
    summary: 'Check required fields, your permissions, and your connection — then retry or reload.',
    answer:
      'A few quick checks clear most "won\'t save" moments:\n\n' +
      '• Required fields — look for a highlighted field or inline message; a save is usually blocked by something missing or invalid.\n' +
      '• Permissions — if an action is greyed out or refused, your role may not allow it; ask an admin to check "User Management".\n' +
      '• Connection — a dropped network can fail a save silently; check you\'re online, then try again.\n' +
      '• Still stuck — reload the page and retry; if it persists, note exactly what you clicked and the message, and report it.',
    related: ['roles-permissions', 'manage-user'],
  },
  {
    id: 'accessibility-keyboard',
    question: 'Can I use the help center and app by keyboard?',
    category: 'Tips & troubleshooting',
    scope: 'both',
    keywords: ['keyboard', 'accessibility', 'shortcuts', 'tab', 'escape', 'screen reader', 'a11y', 'no mouse'],
    summary: 'Yes — Tab moves between controls, Enter opens a topic, and Esc goes back or closes.',
    answer:
      'This help center is fully keyboard-friendly. Tab and Shift+Tab move between the search box, category chips and topic rows; Enter opens the focused topic; and Esc steps back from a topic to the list, or closes the panel from the list. Focus stays trapped inside the panel while it\'s open, so a screen reader won\'t wander off.\n\n' +
      'Across the app, the same conventions hold — Tab to move, Enter to activate, Esc to dismiss dialogs.',
    related: ['what-is-bestacco', 'quick-search'],
  },
];

// ─── Search / filtering ───────────────────────────────────────────────────────

/**
 * Very common words that carry little signal in a search query. Dropped before
 * scoring so conversational phrasing doesn't dilute the match.
 */
const STOPWORDS = new Set([
  'how', 'do', 'i', 'a', 'an', 'the', 'to', 'is', 'are', 'my', 'in', 'on', 'at',
  'where', 'what', 'why', 'does', 'can', 'get', 'set', 'of', 'for', 'me', 'you',
  'it', 'and', 'or', 'with', 'from', 'when', 'this', 'that', 'up', 'we', 'our',
  'be', 'if', 'not', 'no',
]);

export interface ScoredEntry {
  entry: KnowledgeEntry;
  score: number;
}

/**
 * Score one entry against a lowercased query. Higher is better; 0 = no signal.
 * Weighting: a hit in the question > keywords > summary > category > steps/body.
 */
export function scoreEntry(entry: KnowledgeEntry, query: string): number {
  const terms = query
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t && !STOPWORDS.has(t));
  if (terms.length === 0) return 0;

  const question = entry.question.toLowerCase();
  const keywords = entry.keywords.join(' ').toLowerCase();
  const summary = (entry.summary ?? '').toLowerCase();
  const category = entry.category.toLowerCase();
  const body = entry.answer.toLowerCase();
  const steps = (entry.steps ?? []).join(' ').toLowerCase();

  let score = 0;
  let matchedTerms = 0;
  for (const term of terms) {
    let termScore = 0;
    if (question.includes(term)) termScore += 10;
    if (keywords.includes(term)) termScore += 6;
    if (summary.includes(term)) termScore += 5;
    if (category.includes(term)) termScore += 4;
    if (body.includes(term)) termScore += 2;
    if (steps.includes(term)) termScore += 2;
    if (termScore > 0) matchedTerms += 1;
    score += termScore;
  }
  if (matchedTerms === 0) return 0;
  // Reward entries that match a larger share of the meaningful terms.
  score += matchedTerms * 2;
  // Bonus if the whole (trimmed) query is a substring of the question.
  if (question.includes(query.trim().toLowerCase())) score += 8;
  return score;
}

/** All entries with score > 0, best first. */
export function searchKnowledge(query: string): ScoredEntry[] {
  return KNOWLEDGE_BASE.map((entry) => ({ entry, score: scoreEntry(entry, query) }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score);
}

/** Entries that apply to a shell variant: `admin` → admin+both, `pms` → pms+both. */
export function entriesForVariant(variant: 'pms' | 'admin'): KnowledgeEntry[] {
  return KNOWLEDGE_BASE.filter((e) => e.scope === 'both' || e.scope === variant);
}

/**
 * Entries grouped by category in CATEGORY_ORDER, filtered to a variant. Empty
 * categories are omitted.
 */
export function categorizedForVariant(
  variant: 'pms' | 'admin',
): { category: Category; entries: KnowledgeEntry[] }[] {
  const inScope = entriesForVariant(variant);
  return CATEGORY_ORDER.map((category) => ({
    category,
    entries: inScope.filter((e) => e.category === category),
  })).filter((g) => g.entries.length > 0);
}

/** Categories that actually have at least one in-scope entry, in display order. */
export function categoriesForVariant(variant: 'pms' | 'admin'): Category[] {
  return categorizedForVariant(variant).map((g) => g.category);
}
